import { SwellRswExitAbi } from '../abis/swell'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, toNumber, type LstWithdrawalReader } from './shared'

/**
 * Swell rswETH — ERC-721 enumerable rswEXIT NFT. Same enumeration
 * pattern as EtherFi:
 *  1. `balanceOf(user)` → count.
 *  2. `tokenOfOwnerByIndex(user, i)` for i in [0, count) — enumerate
 *     token IDs.
 *  3. `getWithdrawalRequest(id)` per id + a single
 *     `getLastTokenIdProcessed()` for the finalization frontier.
 *
 * A request is claimable iff `tokenId <= lastTokenIdProcessed`.
 *
 * Log-free per `INDEXING_STRATEGIES.md`: ERC721EnumerableUpgradeable
 * is in rswEXIT's inheritance chain (verified via
 * `supportsInterface(0x780e9d63)`).
 */
export const readerSwell: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []

    const stage1 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'balanceOf',
          params: [user],
        },
        {
          address: entry.withdrawalContract,
          name: 'getLastTokenIdProcessed',
          params: [],
        },
      ] as unknown as never[],
      abi: [SwellRswExitAbi, SwellRswExitAbi] as unknown as never,
    })) as unknown[]
    const count = toNumber(stage1[0]) ?? 0
    const lastProcessed = toBigInt(stage1[1]) ?? 0n
    if (count === 0) return []

    const enumCalls = Array.from({ length: count }, (_, i) => ({
      address: entry.withdrawalContract!,
      name: 'tokenOfOwnerByIndex',
      params: [user, BigInt(i)],
    }))
    const stage2 = (await multicallRetry({
      chain: chainId,
      calls: enumCalls as unknown as never[],
      abi: enumCalls.map(() => SwellRswExitAbi) as unknown as never,
    })) as unknown[]
    const ids = stage2.map(toBigInt).filter((v): v is bigint => v !== undefined)
    if (ids.length === 0) return []

    const detailCalls = ids.map((id) => ({
      address: entry.withdrawalContract!,
      name: 'getWithdrawalRequest',
      params: [id],
    }))
    const stage3 = (await multicallRetry({
      chain: chainId,
      calls: detailCalls as unknown as never[],
      abi: detailCalls.map(() => SwellRswExitAbi) as unknown as never,
    })) as unknown[]

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i]
      const r = stage3[i] as Record<string, unknown> | undefined
      if (!r) continue
      const amount = toBigInt(r.amount)
      if (amount === undefined) continue
      const claimable = id <= lastProcessed
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: id.toString(),
        amountUnderlying: amount.toString(),
        status: claimable ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
